import React, { useEffect, useState } from "react";
import {
  getCustomerSalesCancellation,
  getCustomerSalesCancellationProof,
} from "../../../services/sales";
import { FaFilePdf, FaFileImage, FaFileWord } from "react-icons/fa";
import axiosInstance from "../../../services/api/axios";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Spinner, Card } from "@material-tailwind/react";
import { FormModal } from "../../../components/form";
import {
  CloseButton,
  GradientButton,
  OutlineButton,
  PrimaryButton,
} from "../../../components/Buttons";
import SSPPassphraseModal from "../../../components/SSPPassphraseModal";
import ESignStatusBadge from "../../../components/ESignStatusBadge";
import { getSignatureRequests } from "../../../services/esign";

// Fetches the latest eSign signature request for a (sale, documentType) and
// renders its status badge. Fully guarded — renders nothing if none exists.
const ESignStatus = ({ customerSalesId, documentType }) => {
  const [req, setReq] = useState(null);
  useEffect(() => {
    let active = true;
    if (!customerSalesId) return undefined;
    getSignatureRequests(customerSalesId, documentType)
      .then((res) => {
        const list = Array.isArray(res) ? res : res?.items || [];
        if (!active) return;
        setReq(
          list.length
            ? list.reduce((a, b) =>
                new Date(b.createdDate) > new Date(a.createdDate) ? b : a
              )
            : null
        );
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [customerSalesId, documentType]);
  if (!req) return null;
  return (
    <div className="inline-flex items-center gap-2">
      <ESignStatusBadge status={req.status} />
      {req.status === "COMPLETED" && req.signedPdfUrl && (
        <a
          href={req.signedPdfUrl}
          target="_blank"
          rel="noreferrer"
          className="text-xs font-medium text-flamingo-600 hover:underline"
        >
          View signed
        </a>
      )}
    </div>
  );
};

const CancellationDetailsTab = ({ sale, customerSaleId, installments }) => {
  const [cancellationData, setCancellationData] = useState(null);
  const [proofData, setProofData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingProof, setLoadingProof] = useState(true);
  const [error, setError] = useState(null);
  const [errorProof, setErrorProof] = useState(null);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [receiptContent, setReceiptContent] = useState("");
  const [sendLoading, setSendLoading] = useState(false);
  const [currentInstallment, setCurrentInstallment] = useState(null);
  const [refundDetails, setRefundDetails] = useState("");
  const [showQuillEditor, setShowQuillEditor] = useState(false);
  const [cancellationDocsSigned, setCancellationDocsSigned] = useState([]);
  const [cancellationDocsUnsigned, setCancellationDocsUnsigned] = useState([]);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [cancellationLetterFile, setCancellationLetterFile] = useState(null);
  const [uploadingCancellationLetter, setUploadingCancellationLetter] =
    useState(false);
  const [currentDocDetails, setCurrentDocDetails] = useState(null);
  const [currentDocType, setCurrentDocType] = useState(null);
  const [refundReceipts, setRefundReceipts] = useState([]);
  const [loadingRefundReceipts, setLoadingRefundReceipts] = useState(true);
  const [errorRefundReceipts, setErrorRefundReceipts] = useState(null);
  const [showSSPModal, setShowSSPModal] = useState(false);
  const [pendingGenerateDocType, setPendingGenerateDocType] = useState(null);
  const [sspLoading, setSspLoading] = useState(false);

  const fetchCancellationDocs = async () => {
    setLoading(true);
    try {
      const signedResponse = await axiosInstance.get(
        "/customer/sales/cancellation-letters",
        {
          params: {
            customer_sales_id: customerSaleId,
            isSigned: true,
          },
        }
      );
      const unsignedResponse = await axiosInstance.get(
        "/customer/sales/cancellation-letters",
        {
          params: {
            customer_sales_id: customerSaleId,
            isSigned: false,
          },
        }
      );
     
      setCancellationDocsSigned(signedResponse);
      setCancellationDocsUnsigned(unsignedResponse);
    } catch (error) {
      console.error("Error fetching cancellation  documents:", error);
    } finally {
      setLoading(false);
    }
  };

  // Component-scope so post-send handlers can refresh the refund-receipts
  // list immediately (the original copy was trapped inside a useEffect).
  const fetchRefundReceipts = async () => {
    if (!customerSaleId) {
      setLoadingRefundReceipts(false);
      setRefundReceipts([]);
      return;
    }
    try {
      setLoadingRefundReceipts(true);
      const response = await axiosInstance.get(
        `/customer/sales/cancellation/refund`,
        { params: { customer_sales_id: customerSaleId } }
      );
      setRefundReceipts(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error("Error fetching cancellation refund receipts:", error);
      setErrorRefundReceipts(error.message);
    } finally {
      setLoadingRefundReceipts(false);
    }
  };

  const downloadFile = (file) => {
    if (!file || !file.content) {
      return;
    }
    try {
      const byteCharacters = atob(file.content);
      const byteArrays = [];
      for (let offset = 0; offset < byteCharacters.length; offset += 1024) {
        const slice = byteCharacters.slice(offset, offset + 1024);
        const byteNumbers = new Array(slice.length);
        for (let i = 0; i < slice.length; i++) {
          byteNumbers[i] = slice.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        byteArrays.push(byteArray);
      }
      const blob = new Blob(byteArrays, { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.key;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      closeModal(); // close modal after download
    } catch (error) {
      console.error("Error downloading file:", error);
    }
  };

  useEffect(() => {
    const fetchCancellationData = async () => {
      if (customerSaleId) {
        try {
          setLoading(true);
          const cancellationResponse = await getCustomerSalesCancellation({
            customerSalesId: customerSaleId,
          });
          //console.log("cancellation data:", cancellationResponse);
          if (
            cancellationResponse &&
            Array.isArray(cancellationResponse) &&
            cancellationResponse.length > 0
          ) {
            setCancellationData(cancellationResponse[0]);
          } else {
            setCancellationData(null);
          }
        } catch (err) {
          console.error("Error fetching cancellation details:", err);
          setError(err.message);
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
        setCancellationData(null);
      }
    };

    const fetchProofData = async () => {
      if (customerSaleId) {
        try {
          setLoadingProof(true);
          const proofResponse = await getCustomerSalesCancellationProof({
            customer_sales_id: customerSaleId,
          });
          //console.log("proof data:", proofResponse);
          if (
            proofResponse &&
            Array.isArray(proofResponse) &&
            proofResponse.length > 0
          ) {
            setProofData(proofResponse[0]);
          } else {
            setProofData(null);
          }
        } catch (err) {
          console.error("Error fetching cancellation proof:", err);
          setErrorProof(err.message);
        } finally {
          setLoadingProof(false);
        }
      } else {
        setLoadingProof(false);
        setProofData(null);
      }
    };
    fetchCancellationData();
    fetchProofData();
    fetchCancellationDocs();
    fetchRefundReceipts();

    // Set the installment ID when the component mounts or when installments changes
    if (installments && installments.length > 0) {
      const firstInstallment = installments[0];
      if (firstInstallment) {
        setCurrentInstallment(firstInstallment);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerSaleId, installments]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-red-600">Error: {error}</div>
      </div>
    );
  }
  const cancellationStatus = cancellationData?.cancellationStatus;
  const cancellationReason = cancellationData?.cancellationReason;
  const refundAmount = cancellationData?.refundAmount;
  const refundAmountDetails = cancellationData?.refundAmountDetails;
  const cancellationInitiatedDate = cancellationData?.cancellationInitiatedDate;
  const cancellationCompletedDate = cancellationData?.cancellationCompletedDate;
  const proofContent = proofData?.content;
  const proofContentType = proofData?.contentType;
  const proofKey = proofData?.key;

  const handleFileClick = async (file) => {
    setSelectedFile(file);
    setPdfUrl(null);

    if (file && file.content) {
      const byteCharacters = atob(file.content);
      const byteArrays = [];
      for (let offset = 0; offset < byteCharacters.length; offset += 1024) {
        const slice = byteCharacters.slice(offset, offset + 1024);
        const byteNumbers = new Array(slice.length);
        for (let i = 0; i < slice.length; i++) {
          byteNumbers[i] = slice.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        byteArrays.push(byteArray);
      }
      const blob = new Blob(byteArrays, { type: "application/pdf" });
      const pdfBlobUrl = URL.createObjectURL(blob);
      setPdfUrl(pdfBlobUrl);
    }
  };

  const getFileIcon = (contentType) => {
    if (contentType?.includes("pdf")) {
      return <FaFilePdf className="text-red-600 text-xl" />;
    } else if (contentType?.includes("image")) {
      return <FaFileImage className="text-blue-600 text-xl" />;
    } else if (
      contentType?.includes("word") ||
      contentType?.includes("document")
    ) {
      return <FaFileWord className="text-blue-600 text-xl" />;
    }
    return <FaFilePdf className="text-gray-600 text-xl" />;
  };

  const getFileName = (key) => {
    if (!key) return "document";
    const parts = key.split(".");
    return parts.slice(0, -1).join(".");
  };

  const handleCancellationLetter = () => {
    setModalIsOpen(true);

    setCurrentDocType("Refund Letter");
  };

  const handleQuillChange = (content) => {
    setReceiptContent(content);
  };

  const handleGenerateCancellationLetter = async () => {
    setSendLoading(true);
    try {
      const response = await axiosInstance.post("/generate/receipt-preview", {
        notificationType: "CANCELLATION_REFUND",
        customerSalesId: customerSaleId,
        customerSalesInstallmentId:
          currentInstallment?.customerSalesInstallmentId,
        refundAmountDetails: refundDetails,
      });
      setReceiptContent(response);
      setShowQuillEditor(true);
     
    } catch (error) {
      console.error("Error generating cancellation letter:", error);
      toast.error(error.message || "Failed to generate cancellation letter");
    } finally {
      setSendLoading(false);
    }
  };

  const handleRefundSend = async () => {
    setSendLoading(true);
    try {
      await axiosInstance.post("/generate/send/receipt-notification", {
        notificationType: "CANCELLATION_REFUND",
        customerSalesId: customerSaleId,
        content: receiptContent,
      });
      toast.success(`Refund Letter sent successfully`);
      // Refresh the refund-receipts list so the sent letter appears without a reload.
      await fetchRefundReceipts();
      closeModal();
    } catch (error) {
      console.error(`Error sending refund letter:`, error);
      if (
        error.response &&
        error.response.data &&
        error.response.data.detail === "Customer Notification Config not found"
      ) {
        toast.warn("Please configure Customer Notification Settings first.");
      } else {
        if (
          error.response &&
          error.response.data &&
          error.response.data.detail
        ) {
          toast.error(
            error.response.data.detail || `Failed to send Cancellation Letter`
          );
        } else {
          toast.error(`Failed to send Cancellation Letter`);
        }
      }
    } finally {
      setSendLoading(false);
    }
  };

  const handleCancellationLetterUpload = (event) => {
    setCancellationLetterFile(event.target.files[0]);
  };
  const handleGenerateDocument = async (docType) => {
    // Store the docType and show SSP modal first
    setPendingGenerateDocType(docType);
    setShowSSPModal(true);
  };

  const handleSSPConfirm = async (passphrase) => {
    setSspLoading(true);
    try {
      // Verify SSP passphrase with backend
      await axiosInstance.post("/verify/ssp-passphrase", {
        passphrase: passphrase
      });

      // If verification successful, proceed with document generation
      const docType = pendingGenerateDocType;
      setCurrentDocType(docType);
      let notificationType = null;

      if (docType === "Cancellation Letter") {
        notificationType = "CANCELLATION_LETTER";
      }
      
      const response = await axiosInstance.post("/generate/receipt-preview", {
        notificationType: notificationType,
        customerSalesId: sale.customerSalesId,
      });
      
   
      setCurrentDocDetails({
        notificationType,
        customerSalesId: sale.customerSalesId,
        content: response,
      });
      setReceiptContent(response);
      if (docType === "Cancellation Letter") {
        setShowQuillEditor(true);
        setModalIsOpen(true);
      } else {
        setModalIsOpen(true);
      }
    
      
      setShowSSPModal(false);
      setPendingGenerateDocType(null);

    } catch (error) {
      console.error("Error:", error);
      throw new Error(error.response?.data?.message || error.message || "Invalid passphrase or failed to generate document");
    } finally {
      setSspLoading(false);
    }
  };

  const handleSSPClose = () => {
    setShowSSPModal(false);
    setPendingGenerateDocType(null);
    setSspLoading(false);
  };

  const uploadCancellationLetter = async () => {
    if (!cancellationLetterFile) {
      console.error("No cancellation letter file selected.");
      return;
    }
    setUploadingCancellationLetter(true);
    const uploadFormData = new FormData();
    uploadFormData.append("customer_sales_id", customerSaleId);
    uploadFormData.append("medium", cancellationLetterFile);
    try {
      await axiosInstance.post(
        "/customer/sales/cancellation-letters/signed",
        uploadFormData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      toast.success("Cancellation letter uploaded successfully!");
      setCancellationLetterFile(null);
      fetchCancellationDocs();
      // fetch data again to show the updated list
    } catch (error) {
      console.error("Error uploading cancellation letter:", error);
      if (error.response && error.response.data && error.response.data.detail) {
        toast.error(
          error.response.data.detail || "Error uploading cancellation letter."
        );
      } else {
        toast.error("Error uploading cancellation letter.");
      }
    } finally {
      setUploadingCancellationLetter(false);
    }
  };

  const handleSendDocument = async () => {
    setSendLoading(true);
    try {
    
      await axiosInstance.post("/generate/send/receipt-notification", {
        notificationType: currentDocDetails?.notificationType,
        customerSalesId: currentDocDetails?.customerSalesId,
        content: receiptContent,
      });
      toast.success(`${currentDocType} sent successfully`);
      // Refresh cancellation docs so the sent letter shows without a reload.
      await fetchCancellationDocs();
      closeModal();
    } catch (error) {
      console.error(`Error sending ${currentDocType}:`, error);
      if (error.response && error.response.data && error.response.data.detail) {
        toast.error(
          error.response.data.detail || `Failed to send ${currentDocType}`
        );
      } else {
        toast.error(`Failed to send ${currentDocType}`);
      }
    } finally {
      setSendLoading(false);
    }
  };

  const closeModal = () => {
    setModalIsOpen(false);
    setReceiptContent("");
    setShowQuillEditor(false);
    setRefundDetails("");
    setPdfUrl(null);
    setSelectedFile(null);
    setCurrentDocType(null);
    setCurrentDocDetails(null);
  };

  // modalStyle removed — superseded by FormModal which provides the shell.

  return (
    <div className="p-6">
      <div className="flex justify-between">
        <h2 className="text-2xl font-semibold mb-4">Refund Details</h2>
        <div className="flex items-start gap-3">
          <div className="flex flex-col items-start gap-1.5">
            <GradientButton onClick={handleCancellationLetter} className="w-fit">
              Refund Receipt
            </GradientButton>
            <ESignStatus customerSalesId={customerSaleId} documentType="CANCELLATION_REFUND" />
          </div>
          <div className="flex flex-col items-start gap-1.5">
            <GradientButton
              onClick={() => handleGenerateDocument("Cancellation Letter")}
              className="w-fit"
            >
              Cancellation Letter
            </GradientButton>
            <ESignStatus customerSalesId={customerSaleId} documentType="CANCELLATION_LETTER" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="p-4">
          <h6 className="text-lg font-semibold text-blue-gray-700 mb-2">
            Cancellation Information
          </h6>
          {cancellationStatus && (
            <div className="mb-2">
              <p className="text-sm text-gray-500 font-medium">
                Cancellation Status:
              </p>
              <p className="text-blue-gray-800">{cancellationStatus}</p>
            </div>
          )}
          {cancellationReason && (
            <div className="mb-2">
              <p className="text-sm text-gray-500 font-medium">
                Cancellation Reason:
              </p>
              <p className="text-blue-gray-800">{cancellationReason}</p>
            </div>
          )}
          {refundAmount && (
            <div className="mb-2">
              <p className="text-sm text-gray-500 font-medium">
                Refund Amount:
              </p>
              <p className="text-blue-gray-800">₹{refundAmount}</p>
            </div>
          )}
          {refundAmountDetails && (
            <div className="mb-2">
              <p className="text-sm text-gray-500 font-medium">
                Refund Amount Details:
              </p>
              <p className="text-blue-gray-800">{refundAmountDetails}</p>
            </div>
          )}
          {cancellationInitiatedDate && (
            <div className="mb-2">
              <p className="text-sm text-gray-500 font-medium">
                Cancellation Initiated Date:
              </p>
              <p className="text-blue-gray-800">
                {cancellationInitiatedDate
                  ? new Date(cancellationInitiatedDate).toLocaleDateString()
                  : "Not Available"}
              </p>
            </div>
          )}
          {cancellationCompletedDate && (
            <div className="mb-2">
              <p className="text-sm text-gray-500 font-medium">
                Cancellation Completed Date:
              </p>
              <p className="text-blue-gray-800">
                {cancellationCompletedDate
                  ? new Date(cancellationCompletedDate).toLocaleDateString()
                  : "Not Available"}
              </p>
            </div>
          )}
          {!cancellationData && !loading && !error && (
            <div className="text-gray-500">No cancellation initiated yet.</div>
          )}
        </Card>
        <Card className="p-4">
          <div className="flex justify-between ">
            <h6 className="text-lg font-semibold text-blue-gray-700 mb-2">
              Cancellation Proof
            </h6>
          </div>

          <div className="flex flex-col">
            {loadingProof ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
              </div>
            ) : errorProof ? (
              <div className="text-red-500">
                Error Loading Proof: {errorProof}{" "}
              </div>
            ) : proofContent ? (
              <>
                <div
                  key={proofKey}
                  onClick={() => handleFileClick(proofData)}
                  className="cursor-pointer border p-2 rounded hover:bg-gray-100 flex items-center gap-2"
                >
                  {getFileIcon(proofContentType)}
                  {getFileName(proofKey)}
                </div>
                {proofContentType?.includes("image") && (
                  <img
                    src={`data:${proofContentType};base64,${proofContent}`}
                    alt="Cancellation Proof"
                    className="max-w-full h-auto rounded mt-2"
                  />
                )}
              </>
            ) : (
              <div className="text-gray-500">
                No cancellation proof available.
              </div>
            )}
          </div>
        </Card>
      </div>
      <Card className="p-4 mt-4">
        <h6 className="text-lg font-semibold text-blue-gray-700 mb-2">
          Cancellation Documents
        </h6>
        <div className="flex flex-col">
          <div className="mb-4">
            <h4 className="font-normal mb-2">Cancellation letter upload</h4>
            <div className="flex items-center gap-4">
              <input
                type="file"
                accept=".pdf"
                onChange={handleCancellationLetterUpload}
                className="border rounded p-2 w-full"
                disabled={uploadingCancellationLetter}
              />
              <PrimaryButton
                onClick={uploadCancellationLetter}
                disabled={
                  !cancellationLetterFile || uploadingCancellationLetter
                }
                size="sm"
                className={"w-28"}
              >
                {uploadingCancellationLetter ? (
                  <Spinner color="white" size="sm" />
                ) : (
                  "Upload"
                )}
              </PrimaryButton>
            </div>
          </div>
          <div className="space-y-4 mt-6">
            <h5 className="font-normal mt-4">Signed</h5>
            <div className="space-y-2">
              {cancellationDocsSigned?.map((file) => (
                <div
                  key={file.key}
                  onClick={() => handleFileClick(file)}
                  className="cursor-pointer border p-2 rounded hover:bg-gray-100 flex items-center gap-2"
                >
                  <FaFilePdf className="text-red-600 text-xl" />
                  {file.key}
                </div>
              ))}
            </div>
            <h5 className="font-normal mt-4">Unsigned</h5>
            <div className="space-y-2">
              {cancellationDocsUnsigned?.map((file) => (
                <div
                  key={file.key}
                  onClick={() => handleFileClick(file)}
                  className="cursor-pointer border p-2 rounded hover:bg-gray-100 flex items-center gap-2"
                >
                  <FaFilePdf className="text-red-600 text-xl" />
                  {file.key}
                </div>
              ))}
            </div>
          </div>
        </div>
      </Card>
      <Card className="p-4 mt-4">
        <h6 className="text-lg font-semibold text-blue-gray-700 mb-2">
          Refund Receipts
        </h6>
        <div className="flex flex-col">
          {loadingRefundReceipts ? (
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            </div>
          ) : errorRefundReceipts ? (
            <div className="text-red-500">
              Error Loading Refund Receipts: {errorRefundReceipts}{" "}
            </div>
          ) : refundReceipts && refundReceipts.length > 0 ? (
            refundReceipts.map((file) => (
              <div
                key={file.key}
                onClick={() => handleFileClick(file)}
                className="cursor-pointer border p-2 rounded hover:bg-gray-100 flex items-center gap-2"
              >
                {getFileIcon(file.contentType)}
                {getFileName(file.key)}
              </div>
            ))
          ) : (
            <div className="text-gray-500">No Refund Receipts available.</div>
          )}
        </div>
      </Card>
      {/* Cancellation / Refund letter preview modal — single shell, body
          branches on currentDocType. */}
      <FormModal
        open={modalIsOpen}
        onClose={closeModal}
        title={`${currentDocType === "Cancellation Letter" ? "Cancellation Letter" : "Refund Letter"} Preview`}
        size="xl"
      >
            {currentDocType === "Cancellation Letter" ? (
              <>
                <p className="text-gray-600 mb-4">
                  Review the cancellation letter and click send to notify the
                  user, or close to cancel.
                </p>
                <div className="flex flex-col gap-4">
                  {showQuillEditor && (
                    <ReactQuill
                      value={receiptContent}
                      onChange={handleQuillChange}
                      theme="snow"
                    />
                  )}
                </div>
                <div className="text-right mt-4 flex justify-end gap-4">
                  {/* <OutlineButton onClick={closeModal} className="w-32">
                    Close
                  </OutlineButton> */}
                  <PrimaryButton
                    onClick={handleSendDocument}
                    className={"w-32"}
                    disabled={sendLoading}
                  >
                    {sendLoading ? "Sending..." : "Send"}
                  </PrimaryButton>
                </div>
              </>
            ) : (
              <>
                <p className="text-gray-600 mb-4">
                  {showQuillEditor
                    ? "Review the refund receipt and click send to notify the user, or close to cancel."
                    : "Enter the refund details and click generate."}
                </p>
                {!showQuillEditor && (
                  <div className="mb-4">
                    <label
                      className="block text-gray-700 text-sm font-bold mb-2"
                      htmlFor="refundDetails"
                    >
                      Refund Details:
                    </label>
                    <textarea
                      id="refundDetails"
                      className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                      value={refundDetails}
                      onChange={(e) => setRefundDetails(e.target.value)}
                    />
                  </div>
                )}

                {showQuillEditor && (
                  <div className="flex flex-col gap-4">
                    <ReactQuill
                      value={receiptContent}
                      onChange={handleQuillChange}
                      theme="snow"
                    />
                  </div>
                )}
                <div className="text-right mt-4 flex justify-end gap-4">
                 
                  {!showQuillEditor ? (
                    <PrimaryButton
                      onClick={handleGenerateCancellationLetter}
                      className={"w-32"}
                      disabled={sendLoading}
                    >
                      {sendLoading ? "Generating..." : "Generate"}
                    </PrimaryButton>
                  ) : (
                    <PrimaryButton
                      onClick={handleRefundSend}
                      className={"w-32"}
                      disabled={sendLoading}
                    >
                      {sendLoading ? "Sending..." : "Send"}
                    </PrimaryButton>
                  )}
                </div>
              </>
            )}
      </FormModal>
      {pdfUrl && (
        <div className="w-1/3 p-4 fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white shadow-lg rounded-lg z-50">
          <div className="">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-semibold">{selectedFile?.key}</h4>
              {/* <Button
                onClick={() => downloadFile(selectedFile)}
                size="sm"
                className="flex items-center gap-2 bg-accent"
              >
                <Download className="h-4 w-4" />
                Download
              </Button> */}
            
              <CloseButton onClick={closeModal} />

            </div>
            <div className="border p-4 rounded mt-4">
              <iframe
                src={pdfUrl}
                title="PDF Preview"
                width="100%"
                height="500px"
                style={{ border: "none" }}
              />
            </div>
          </div>
          <div className="text-right mt-4">
            <OutlineButton
              size="sm"
              variant="outline"
              onClick={() => downloadFile(selectedFile)}
              color="white"
              className="p-2 "
            >
              Download
            </OutlineButton>
          </div>
        </div>
      )}

      {/* SSP Passphrase Modal */}
      <SSPPassphraseModal
        isOpen={showSSPModal}
        onClose={handleSSPClose}
        onConfirm={handleSSPConfirm}
        loading={sspLoading}
      />
    </div>
  );
};

export default CancellationDetailsTab;